import { setExternalBrowserState } from '@common/redux/slice/page';
import store from '@common/redux/store';
import { EmitterSubscription, Keyboard, Platform } from 'react-native';

/**
 * Tracks whether an in-app browser (SFSafariViewController on iOS, Custom Tabs
 * on Android) is presented over the app, so that layout-sensitive views can opt
 * out of reacting to it. Read it through `keyboardAvoidingEnabledSelector`.
 *
 * Why this exists: iOS keyboard notifications are process-global. When the user
 * types inside the in-app browser, `keyboardWillChangeFrame` is still delivered
 * to this app's JS, with an `endCoordinates` frame that is not in the app view's
 * coordinate space (the browser hosts its web content out of process).
 * `KeyboardAvoidingView` never checks `isEventFromThisApp`, so it stores a bogus
 * bottom offset -- roughly the full screen height -- which blanks the screen,
 * and keeps it until the next keyboard event arrives. Setting `enabled={false}`
 * makes it ignore that offset while the browser is up; the offset itself is only
 * known-good again once a keyboard event that really belongs to this app has
 * been processed, which is what `isAwaitingKeyboardResync` waits for.
 */

/** Handle for the listener that waits for this app to own the keyboard again. */
let resyncSubscription: EmitterSubscription | null = null;

const stopWaitingForKeyboardResync = () => {
  resyncSubscription?.remove();
  resyncSubscription = null;
};

export const markExternalBrowserOpened = () => {
  stopWaitingForKeyboardResync();
  store.dispatch(
    setExternalBrowserState({ isOpen: true, isAwaitingKeyboardResync: false })
  );
};

export const markExternalBrowserClosed = () => {
  stopWaitingForKeyboardResync();

  // Only iOS delivers another app's keyboard events to us. On Android the
  // browser is a separate Activity, so nothing needs to be re-synced.
  if (Platform.OS !== 'ios') {
    store.dispatch(
      setExternalBrowserState({
        isOpen: false,
        isAwaitingKeyboardResync: false,
      })
    );
    return;
  }

  store.dispatch(
    setExternalBrowserState({ isOpen: false, isAwaitingKeyboardResync: true })
  );

  resyncSubscription = Keyboard.addListener(
    'keyboardWillChangeFrame',
    (event) => {
      // Events raised by the browser we just dismissed are not ours; keep waiting.
      if ((event as any)?.isEventFromThisApp === false) {
        return;
      }
      stopWaitingForKeyboardResync();
      store.dispatch(
        setExternalBrowserState({
          isOpen: false,
          isAwaitingKeyboardResync: false,
        })
      );
    }
  );
};
